import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core'
import { FlexLayoutModule } from '@angular/flex-layout'
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { EstimateCategoryModel, EstimateCategoryStatus } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

export interface EstimateCategoryDialogData {
  estimateCategory?: EstimateCategoryModel
  categories: EstimateCategoryModel[]
  initialParentId?: string | null
}

export interface EstimateCategoryDialogResult {
  name: string
  parentId: string | null
  status: EstimateCategoryStatus
}

@Component({
  selector: 'mmx-estimate-categories-dialog',
  templateUrl: './estimate-categories-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    TranslateModule,
  ],
})
export class EstimateCategoriesDialogComponent implements OnInit {
  public readonly statuses = [
    EstimateCategoryStatus.ACTIVE,
    EstimateCategoryStatus.INACTIVE,
  ]

  public readonly form = this.fb.group({
    name: this.fb.control(this.data.estimateCategory?.name || '', Validators.required),
    status: this.fb.control<EstimateCategoryStatus>(
      this.data.estimateCategory?.status || EstimateCategoryStatus.ACTIVE,
      Validators.required,
    ),
  })

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: EstimateCategoryDialogData,
    private dialogRef: MatDialogRef<EstimateCategoriesDialogComponent>,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.form.controls.name.addValidators(this.createCategoryNameValidator())
    this.form.controls.name.updateValueAndValidity({ emitEvent: false })
  }

  onSubmit() {
    const value = this.form.getRawValue()

    if (this.form.invalid) {
      return
    }

    this.dialogRef.close({
      name: String(value.name || '').trim(),
      parentId: this.getResolvedParentId(),
      status: value.status || EstimateCategoryStatus.ACTIVE,
    })
  }

  private createCategoryNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const parent = this.getSelectedParentCategory()
      const currentCategoryId = this.data.estimateCategory?.id || null
      const targetParentId = this.getResolvedParentId()
      const name = this.normalizeName(control.value)
      const parentName = this.normalizeName(parent?.name)
      const hasSameNameAsParent = !!parent && !!name && name === parentName
      const hasDuplicateName = !!name && this.data.categories.some((category) => {
        if (category.id === currentCategoryId) {
          return false
        }

        return this.normalizeName(category.name) === name
          && (category.parentId || null) === targetParentId
      })

      const errors = {
        ...(hasSameNameAsParent ? { sameAsParent: true } : {}),
        ...(hasDuplicateName ? { duplicateName: true } : {}),
      }

      return Object.keys(errors).length > 0 ? errors : null
    }
  }

  private getSelectedParentCategory() {
    const parentId = this.getResolvedParentId()

    if (!parentId) {
      return undefined
    }

    return this.data.categories.find((category) => category.id === parentId)
  }

  private normalizeName(value: unknown) {
    return String(value || '').trim().toLowerCase()
  }

  private getResolvedParentId(): string | null {
    return this.data.estimateCategory?.parentId || this.data.initialParentId || null
  }
}
